import { useContext, useRef } from "react";
import { InvoiceContext } from "../InvoiceProvider";
import { useReactToPrint } from "react-to-print";
import "../../../../../styles/workspace_styles/TenantAdministratorStyles/Invoices/Invoice/InvoicePreview.css";

const PAGE_SIZE = 20;

const InvoicePreviewHeader = () => {
    const { selectedInvoice } = useContext(InvoiceContext);
    return (
        <div className="tenant-administrator-invoice-preview-header">
            <div className="logo-section">
                <img src={`data:image/png;base64,${selectedInvoice?.companyDetails?.logo}`} />
            </div>
            <div>
                <div className="company-details-section">
                    <h1>INVOICE</h1>
                    <h2>{selectedInvoice?.companyDetails?.name}</h2>
                    <p>{selectedInvoice?.companyDetails?.location}</p>
                    <p>{selectedInvoice?.companyDetails?.street}</p>
                    <p>{selectedInvoice?.companyDetails?.city}</p>
                    <p>{selectedInvoice?.companyDetails?.country}</p>
                </div>
                <div className="company-contact-section">
                    <p>Mobile: {selectedInvoice?.companyDetails?.phoneNumber}</p>
                    <p>{selectedInvoice?.companyDetails?.websiteAddress}</p>
                </div>
            </div>
        </div>
    );
};

const InvoicePreviewFullHeader = () => {
    const { selectedInvoice } = useContext(InvoiceContext);
    const formatDateForPreview = (date) =>
        date ? new Date(date.split("-").reverse().join("-")).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

    if (!selectedInvoice) return null;

    return (
        <>
            <InvoicePreviewHeader />
            <div className="tenant-administrator-invoice-preview-sub-header">
                <div>
                    <p>BILL TO</p>
                    <p>{selectedInvoice?.customerName}</p>
                </div>
                <div className="invoice-primary-details-section">
                    <p>Invoice Number:</p>
                    <p>{selectedInvoice?.invoiceNumber}</p>
                    <p>Invoice Date:</p>
                    <p>{formatDateForPreview(selectedInvoice?.startDate)}</p>
                    <p>Payment Due:</p>
                    <p>{formatDateForPreview(selectedInvoice?.dueDate)}</p>
                    <p>Amount Due (KES):</p>
                    <p className="total-price-p">
                        SH {Number(selectedInvoice?.totalPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>
            </div>
        </>
    );
};

const InvoicePreview = ({ selectedInvoiceTab }) => {
    const { selectedInvoice } = useContext(InvoiceContext);
    const invoiceRef = useRef();
    
    const printInvoice = useReactToPrint({
        contentRef: invoiceRef,
        documentTitle: `Invoice ${selectedInvoice?.invoiceNumber || ""}`,
        pageStyle: `
            @page { size: A4; margin: 5mm; }
            body { font-family: Arial, sans-serif; -webkit-print-color-adjust: exact; color-adjust: exact; }
            .tenant-administrator-invoice-preview-container { 
                page-break-after: always;
                width: 97vw!important;
                box-shadow: none!important;
            }
            .tenant-administrator-invoice-preview-sub-header{
                padding-top: 8px!important;
                padding-bottom: 6px!important;
            }
            .invoice-primary-details-section p{
                margin-top: 5px!important;
                margin-bottom: 5px!important;
            }
            .tenant-administrator-invoice-preview-devices-subscriptions table tr {
                font-size: 16px!important;
                page-break-inside: avoid;
            }
            .tenant-administrator-invoice-preview-devices-subscriptions table td {
                padding-top: 5px!important;
                padding-bottom: 5px!important;
            }
        `,
    });
    if (!selectedInvoice) return null;

    const devices = selectedInvoice?.devicesSubscriptions || [];
    const totalPages = Math.ceil(devices.length / PAGE_SIZE);
    const pages = Array.from({ length: totalPages }, (_, i) =>
        devices.slice(i * PAGE_SIZE, (i + 1) * PAGE_SIZE)
    );

    return (
        <div
            className={`tenant-administrator-invoice-preview-parent-container ${
                selectedInvoiceTab === "default"
                    ? "default"
                    : selectedInvoiceTab === "preview"
                    ? "active"
                    : "inactive"
            }`}
        >
            <button
                className="tenant-administrator-invoice-preview-print-button"
                title="Print invoice"
                onClick={printInvoice}>
                <img src={`${process.env.PUBLIC_URL}/images/print-icon.png`}/>
            </button>
            <div ref={invoiceRef}>
                {pages.map((pageDevices, pageIndex) => (
                    <div
                        className="tenant-administrator-invoice-preview-container"
                        key={pageIndex}
                        style={{ pageBreakAfter: pageIndex < totalPages - 1 ? "always" : "auto" }}
                    >
                        {/* Full header only on first page */}
                        {pageIndex === 0 ? <InvoicePreviewFullHeader /> : <InvoicePreviewHeader />}

                        {/* Devices table */}
                        <div className="tenant-administrator-invoice-preview-devices-subscriptions">
                            <table>
                                <thead>
                                    <tr id="tenant-administrator-invoice-preview-devices-subscriptions-header-strip">
                                        <th>Item</th>
                                        <th>Price</th>
                                        <th>Tax</th>
                                        <th>
                                            <div>Amount</div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageDevices.map((device, index) => (
                                        <tr key={device.subscriptionId}>
                                            <td>{pageIndex * PAGE_SIZE + index + 1}. {device.deviceName}</td>
                                            <td>
                                                {Number(device.planPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td>
                                                {(Number(device.planPrice) * Number(device.planTaxRate)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td>
                                                <div>{Number(device.planTotalPrice).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Summary and notes only on last page */}
                        {pageIndex === totalPages - 1 && (
                            <>
                                <div className="tenant-administrator-invoice-preview-price-summary-parent-container">
                                    <div className="tenant-administrator-invoice-preview-price-summary-container">
                                        <div className="sub-total-container">
                                            <p>Subtotal</p>
                                            <p>
                                                SH {Number(selectedInvoice?.price).toLocaleString("en-US", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </p>
                                        </div>
                                        <div className="tax-container">
                                            <p>Tax</p>
                                            <p>
                                                SH {Number(selectedInvoice?.tax).toLocaleString("en-US", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </p>
                                        </div>
                                        <div className="total-container">
                                            <p>Total</p>
                                            <p>
                                                SH {Number(selectedInvoice?.totalPrice).toLocaleString("en-US", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </p>
                                        </div>
                                        <div className="amount-due-container">
                                            <p>Amount due (KES)</p>
                                            <p>
                                                SH {Number(selectedInvoice?.totalPrice).toLocaleString("en-US", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="tenant-administrator-invoice-preview-notes-container">
                                    <h2>Notes/Terms</h2>
                                    {selectedInvoice?.notes.map((note, index) => (
                                        <div className="notes-entry" key={index}>
                                            {index + 1}. {note}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default InvoicePreview;
